import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Building2,
  CalendarDays,
  Clock3,
  GraduationCap,
  Headphones,
  LockKeyhole,
  LoaderCircle,
  PhoneCall,
  ShieldCheck,
  Sun,
  Sunrise,
  X,
} from 'lucide-react';

// Static colors go through inline `style` (no hover/selected/invalid variant).
// Colors that change on hover/focus/selection go through Tailwind arbitrary
// classes instead, since an inline style always wins over a CSS hover rule.
const COLORS = {
  primary: '#0d5ef4',
  primarySoft: '#eaf2ff',
  ink: '#172b4d',
  muted: '#7a869a',
  border: '#d9e2ec',
  surface: '#ffffff',
  overlay: 'rgba(20, 24, 32, 0.64)',
  success: '#138a4b',
  successSoft: '#e8f7ef',
  danger: '#c6284f',
};

const DEFAULT_COUNTRY_OPTIONS = [
  { label: 'ID', dialCode: '+62' },
  { label: 'IN', dialCode: '+91' },
  { label: 'US', dialCode: '+1' },
  { label: 'SG', dialCode: '+65' },
  { label: 'MY', dialCode: '+60' },
];

const PREFERRED_CALL_TIMES = [
  { value: 'morning', label: 'Morning', Icon: Sunrise },
  { value: 'afternoon', label: 'Afternoon', Icon: Sun },
  { value: 'evening', label: 'Evening', Icon: Building2 },
  { value: 'weekend', label: 'Weekend', Icon: CalendarDays },
];

const INITIAL_FORM_VALUES = {
  fullName: '',
  countryCode: '+62',
  phone: '',
  email: '',
  preferredCallTime: 'morning',
};

const SUCCESS_MESSAGE = "Thanks! Our HR team will call you within 2 hours.";

const normalizePhone = (value) => value.replace(/[^\d\s()+-]/g, '').slice(0, 18);

const isValidEmail = (value) =>
  !value.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const getValidationErrors = (values) => {
  const errors = {};
  const phoneDigits = values.phone.replace(/\D/g, '');

  if (!values.fullName.trim()) {
    errors.fullName = 'Please enter your full name.';
  }

  if (!phoneDigits || phoneDigits.length < 7) {
    errors.phone = 'Please enter a valid phone number.';
  }

  if (!isValidEmail(values.email)) {
    errors.email = 'Enter a valid email address.';
  }

  if (!values.preferredCallTime) {
    errors.preferredCallTime = 'Select a preferred call time.';
  }

  return errors;
};

const inputBorderClass = (hasError) =>
  hasError
    ? 'border-[#c6284f]'
    : 'border-[#d9e2ec] hover:border-[#b8c6d6] focus:border-[#0d5ef4] focus:ring-[3px] focus:ring-[#0d5ef4]/[0.22]';

const HrContactModal = ({ isOpen, onClose, triggerRef, courseLabel, countryOptions, submitContact }) => {
  const dialogRef = useRef(null);
  const firstFieldRef = useRef(null);
  const formId = useId().replace(/:/g, '');
  const [values, setValues] = useState(INITIAL_FORM_VALUES);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [submitState, setSubmitState] = useState('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const selectedCountry =
    countryOptions.find((country) => country.dialCode === values.countryCode) || countryOptions[0];

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const appElement = document.querySelector('.app');
    const previousAriaHidden = appElement?.getAttribute('aria-hidden');

    document.body.style.overflow = 'hidden';
    appElement?.setAttribute('aria-hidden', 'true');

    const focusFirstField = window.requestAnimationFrame(() => {
      firstFieldRef.current?.focus();
    });

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusableElements = dialogRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      );
      const focusable = Array.from(focusableElements);
      if (!focusable.length) return;

      const firstElement = focusable[0];
      const lastElement = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFirstField);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;

      if (appElement) {
        if (previousAriaHidden === null) {
          appElement.removeAttribute('aria-hidden');
        } else {
          appElement.setAttribute('aria-hidden', previousAriaHidden);
        }
      }

      triggerRef.current?.focus();
    };
  }, [isOpen, onClose, triggerRef]);

  const updateErrorsForField = (nextValues, fieldName) => {
    const nextErrors = getValidationErrors(nextValues);
    setErrors((currentErrors) => ({ ...currentErrors, [fieldName]: nextErrors[fieldName] }));
  };

  const handleChange = (event) => {
    const { name } = event.target;
    const nextValue = name === 'phone' ? normalizePhone(event.target.value) : event.target.value;
    const nextValues = { ...values, [name]: nextValue };

    setValues(nextValues);
    setStatusMessage('');

    if (touched[name]) {
      updateErrorsForField(nextValues, name);
    }
  };

  const handleBlur = (fieldName) => {
    const nextErrors = getValidationErrors(values);
    setTouched((currentTouched) => ({ ...currentTouched, [fieldName]: true }));
    setErrors((currentErrors) => ({ ...currentErrors, [fieldName]: nextErrors[fieldName] }));
  };

  const focusFirstInvalidField = (validationErrors) => {
    const firstInvalidField = ['fullName', 'phone', 'email', 'preferredCallTime'].find(
      (fieldName) => validationErrors[fieldName],
    );

    if (!firstInvalidField) return;

    window.requestAnimationFrame(() => {
      if (firstInvalidField === 'preferredCallTime') {
        dialogRef.current?.querySelector('input[name="preferredCallTime"]')?.focus();
        return;
      }

      document.getElementById(`${formId}-${firstInvalidField}`)?.focus();
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = getValidationErrors(values);
    setTouched({
      fullName: true,
      phone: true,
      email: Boolean(values.email.trim()),
      preferredCallTime: true,
    });
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      focusFirstInvalidField(validationErrors);
      return;
    }

    setSubmitState('submitting');
    setStatusMessage('');

    const payload = {
      fullName: values.fullName.trim(),
      countryCode: selectedCountry?.dialCode || values.countryCode,
      phone: values.phone.trim(),
      email: values.email.trim(),
      preferredCallTime: values.preferredCallTime,
      course: courseLabel,
      sourcePath: window.location.pathname,
    };

    try {
      const result = await submitContact(payload);

      if (result?.success === false) {
        throw new Error(result.message || 'Something went wrong. Please try again.');
      }

      setSubmitState('success');
      setStatusMessage(result?.message || SUCCESS_MESSAGE);
    } catch (error) {
      setSubmitState('error');
      setStatusMessage(error.message || 'Something went wrong. Please try again.');
    }
  };

  const handleTryAgain = () => {
    setSubmitState('idle');
    setStatusMessage('');
  };

  const fieldId = (fieldName) => `${formId}-${fieldName}`;
  const errorId = (fieldName) => `${fieldId(fieldName)}-error`;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 font-jost">
      <div
        className="absolute inset-0 animate-fade-in backdrop-blur-[6px]"
        style={{ background: COLORS.overlay }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
        aria-hidden="true"
      />

      <section
        ref={dialogRef}
        id="hr-contact-dialog"
        className="relative flex w-[min(552px,calc(100vw-24px))] max-h-[calc(100dvh-32px)] flex-col overflow-hidden rounded-[22px] animate-fade-in"
        style={{ color: COLORS.ink, background: COLORS.surface, boxShadow: '0 24px 70px rgba(11, 19, 35, 0.28)' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${formId}-title`}
        aria-describedby={`${formId}-description`}
      >
        <header
          className="flex-none px-[22px] pt-[18px] pb-[17px]"
          style={{ color: COLORS.surface, background: COLORS.primary }}
        >
          <div className="flex items-center justify-between gap-3.5">
            <div className="flex min-w-0 items-center gap-2.5">
              <Headphones className="h-6 w-6" aria-hidden="true" />
              <h2 id={`${formId}-title`} className="mt-2 overflow-hidden text-white text-ellipsis whitespace-nowrap text-[1.22rem] font-bold leading-[1.2]">
                Talk with our HR Team
              </h2>
            </div>
            <button
              type="button"
              className="inline-flex h-[34px] w-[34px] flex-none items-center justify-center rounded-full bg-white/[0.18] text-white transition-[background-color,transform] duration-150 hover:rotate-[5deg] hover:bg-white/[0.28] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-white/40 focus-visible:ring-offset-2"
              onClick={onClose}
              aria-label="Close counselor form"
              title="Close counselor form"
            >
              <X className="h-[19px] w-[19px]" aria-hidden="true" />
            </button>
          </div>

          <p
            id={`${formId}-description`}
            className="mt-[5px] flex items-center gap-1.5 text-[0.82rem] leading-[1.3]"
            style={{ color: 'rgba(255, 255, 255, 0.92)' }}
          >
            <Clock3 className="h-[15px] w-[15px]" aria-hidden="true" />
            We&apos;ll call within 2 hours, in your time zone
          </p>

          <span className="mt-3.5 inline-flex max-w-full items-center gap-1.5 overflow-hidden text-ellipsis whitespace-nowrap rounded-full bg-white/[0.18] px-3 py-1.5 text-[0.75rem] font-bold leading-[1.2] text-white">
            <GraduationCap className="h-[15px] w-[15px]" aria-hidden="true" />
            {courseLabel}
          </span>
        </header>

        {submitState === 'success' ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center px-6 pt-[34px] pb-[38px] text-center" role="status" aria-live="polite">
            <div
              className="mb-[15px] flex h-[58px] w-[58px] items-center justify-center rounded-full"
              style={{ color: COLORS.success, background: COLORS.successSoft }}
              aria-hidden="true"
            >
              <PhoneCall className="h-[27px] w-[27px]" />
            </div>
            <h3 className="text-[1.25rem] leading-[1.3]" style={{ color: COLORS.ink }}>Request received</h3>
            <p className="mt-2 max-w-[340px] text-[0.9rem] leading-[1.55]" style={{ color: COLORS.muted }}>{statusMessage}</p>
            <button
              type="button"
              className="mt-[22px] min-h-[44px] rounded-[9px] bg-[#eaf2ff] px-[17px] py-2.5 text-[0.85rem] font-bold text-[#0d5ef4] transition-colors duration-150 hover:bg-[#dbe8ff] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[#0d5ef4]/[0.22] focus-visible:ring-offset-2"
              onClick={handleTryAgain}
            >
              Send another request
            </button>
          </div>
        ) : (
          <form
            className="min-h-0 overflow-y-auto px-[22px] pt-[22px] pb-6"
            style={{ background: COLORS.surface, scrollbarWidth: 'thin', scrollbarColor: `${COLORS.border} transparent` }}
            onSubmit={handleSubmit}
            noValidate
          >
            <div className="mb-[13px] min-w-0 border-0 p-0">
              <label className="mb-1.5 block text-[0.76rem] font-bold leading-[1.25] tracking-[0.035em]" style={{ color: COLORS.ink }} htmlFor={fieldId('fullName')}>
                FULL NAME <span aria-hidden="true" style={{ color: COLORS.primary }}>*</span>
              </label>
              <input
                ref={firstFieldRef}
                id={fieldId('fullName')}
                name="fullName"
                type="text"
                value={values.fullName}
                onChange={handleChange}
                onBlur={() => handleBlur('fullName')}
                placeholder="Your full name"
                autoComplete="name"
                required
                aria-invalid={Boolean(errors.fullName)}
                aria-describedby={errors.fullName ? errorId('fullName') : undefined}
                className={`w-full min-h-[47px] rounded-[10px] border px-[13px] py-3 outline-none transition-[border-color,box-shadow,background-color] duration-150 placeholder:text-[#8c99aa] ${inputBorderClass(errors.fullName)}`}
                style={{ color: COLORS.ink, background: COLORS.surface }}
              />
              {errors.fullName && (
                <p id={errorId('fullName')} className="mt-[5px] text-[0.74rem] leading-[1.3]" style={{ color: COLORS.danger }} role="alert">
                  {errors.fullName}
                </p>
              )}
            </div>

            <div className="mb-[13px] min-w-0 border-0 p-0">
              <label className="mb-1.5 block text-[0.76rem] font-bold leading-[1.25] tracking-[0.035em]" style={{ color: COLORS.ink }} htmlFor={fieldId('phone')}>
                WHATSAPP / PHONE <span aria-hidden="true" style={{ color: COLORS.primary }}>*</span>
              </label>
              <div className={`grid grid-cols-[99px_minmax(0,1fr)] overflow-hidden rounded-[10px] border transition-[border-color,box-shadow] duration-150 focus-within:border-[#0d5ef4] focus-within:ring-[3px] focus-within:ring-[#0d5ef4]/[0.22] ${errors.phone ? 'border-[#c6284f]' : 'border-[#d9e2ec]'}`}>
                <select
                  id={fieldId('countryCode')}
                  name="countryCode"
                  value={values.countryCode}
                  onChange={handleChange}
                  aria-label="Country code"
                  className="min-w-0 border-0 border-r px-[9px] text-[0.88rem] font-medium"
                  style={{ color: COLORS.ink, borderRightColor: COLORS.border, background: COLORS.surface }}
                >
                  {countryOptions.map((country) => (
                    <option key={`${country.label}-${country.dialCode}`} value={country.dialCode}>
                      {country.label} {country.dialCode}
                    </option>
                  ))}
                </select>
                <input
                  id={fieldId('phone')}
                  name="phone"
                  type="tel"
                  value={values.phone}
                  onChange={handleChange}
                  onBlur={() => handleBlur('phone')}
                  placeholder="Phone number"
                  autoComplete="tel"
                  inputMode="tel"
                  required
                  aria-invalid={Boolean(errors.phone)}
                  aria-describedby={errors.phone ? errorId('phone') : undefined}
                  className="min-w-0 border-0 px-[13px] py-3 outline-none placeholder:text-[#8c99aa]"
                  style={{ color: COLORS.ink, background: COLORS.surface }}
                />
              </div>
              {errors.phone && (
                <p id={errorId('phone')} className="mt-[5px] text-[0.74rem] leading-[1.3]" style={{ color: COLORS.danger }} role="alert">
                  {errors.phone}
                </p>
              )}
            </div>

            <div className="mb-[13px] min-w-0 border-0 p-0">
              <label className="mb-1.5 block text-[0.76rem] font-bold leading-[1.25] tracking-[0.035em]" style={{ color: COLORS.ink }} htmlFor={fieldId('email')}>
                EMAIL <span className="font-medium normal-case tracking-normal" style={{ color: COLORS.muted }}>optional</span>
              </label>
              <input
                id={fieldId('email')}
                name="email"
                type="email"
                value={values.email}
                onChange={handleChange}
                onBlur={() => handleBlur('email')}
                placeholder="your@email.com"
                autoComplete="email"
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? errorId('email') : undefined}
                className={`w-full min-h-[47px] rounded-[10px] border px-[13px] py-3 outline-none transition-[border-color,box-shadow,background-color] duration-150 placeholder:text-[#8c99aa] ${inputBorderClass(errors.email)}`}
                style={{ color: COLORS.ink, background: COLORS.surface }}
              />
              {errors.email && (
                <p id={errorId('email')} className="mt-[5px] text-[0.74rem] leading-[1.3]" style={{ color: COLORS.danger }} role="alert">
                  {errors.email}
                </p>
              )}
            </div>

            <fieldset className="mt-[17px] mb-3.5 min-w-0 border-0 p-0">
              <legend className="mb-1.5 block text-[0.76rem] font-bold leading-[1.25] tracking-[0.035em]" style={{ color: COLORS.ink }}>
                BEST TIME TO CALL <span className="font-medium normal-case tracking-normal" style={{ color: COLORS.muted }}>your local time</span>
              </legend>
              <div
                className="grid grid-cols-2 gap-2 sm:grid-cols-4"
                role="radiogroup"
                aria-describedby={errors.preferredCallTime ? errorId('preferredCallTime') : undefined}
              >
                {PREFERRED_CALL_TIMES.map(({ value, label, Icon }, index) => {
                  const isSelected = values.preferredCallTime === value;
                  const optionColorClasses = isSelected
                    ? 'border-[#0d5ef4] bg-[#eaf2ff] text-[#0d5ef4]'
                    : errors.preferredCallTime
                      ? 'border-[#c6284f] bg-white text-[#172b4d] hover:border-[#0d5ef4]'
                      : 'border-[#d9e2ec] bg-white text-[#172b4d] hover:border-[#0d5ef4]';

                  return (
                    <label
                      key={value}
                      className={`flex min-h-[48px] min-w-0 cursor-pointer items-center justify-center gap-1.5 rounded-[10px] border px-[5px] py-[7px] text-center text-[0.76rem] font-semibold leading-[1.15] transition-[color,background-color,border-color,transform] duration-150 hover:-translate-y-px focus-within:ring-[3px] focus-within:ring-[#0d5ef4]/[0.22] ${optionColorClasses}`}
                    >
                      <input
                        type="radio"
                        name="preferredCallTime"
                        value={value}
                        checked={isSelected}
                        onChange={handleChange}
                        required={index === 0}
                        className="absolute h-px w-px overflow-hidden whitespace-nowrap"
                        style={{ clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)' }}
                      />
                      <Icon className="h-[17px] w-[17px]" aria-hidden="true" />
                      <span>{label}</span>
                    </label>
                  );
                })}
              </div>
              {errors.preferredCallTime && (
                <p id={errorId('preferredCallTime')} className="mt-[5px] text-[0.74rem] leading-[1.3]" style={{ color: COLORS.danger }} role="alert">
                  {errors.preferredCallTime}
                </p>
              )}
            </fieldset>

            <button
              type="submit"
              className={`mt-0.5 flex min-h-[56px] w-full items-center justify-center gap-2 rounded-xl bg-[#0d5ef4] px-[18px] py-3.5 text-base font-bold text-white shadow-[0_9px_20px_rgba(13,94,244,0.18)] transition-[background-color,box-shadow,transform] duration-150 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[#0d5ef4]/[0.22] focus-visible:ring-offset-2 ${
                submitState === 'submitting'
                  ? 'cursor-wait opacity-[0.76]'
                  : 'hover:-translate-y-px hover:bg-[#004b82] hover:shadow-[0_12px_24px_rgba(13,94,244,0.24)] active:translate-y-0'
              }`}
              disabled={submitState === 'submitting'}
            >
              {submitState === 'submitting' ? (
                <LoaderCircle className="h-[18px] w-[18px] animate-spin" aria-hidden="true" />
              ) : (
                <Headphones className="h-[18px] w-[18px]" aria-hidden="true" />
              )}
              <span>{submitState === 'submitting' ? 'Calling...' : 'Talk with Counselor'}</span>
            </button>

            {submitState === 'error' && (
              <p className="mt-2.5 text-[0.78rem] leading-[1.4]" style={{ color: COLORS.danger }} role="alert">
                {statusMessage}
                <button type="button" className="ml-1.5 p-0 font-bold underline" style={{ color: COLORS.primary }} onClick={handleTryAgain}>
                  Try again
                </button>
              </p>
            )}

            <div
              className="mt-3.5 flex flex-wrap items-center justify-center gap-x-[11px] gap-y-2 text-[0.72rem] leading-[1.3]"
              style={{ color: COLORS.muted }}
              aria-label="Privacy and callback assurances"
            >
              <span className="inline-flex items-center gap-1 whitespace-nowrap"><LockKeyhole className="h-[14px] w-[14px]" aria-hidden="true" /> Secure</span>
              <i aria-hidden="true" className="h-[3px] w-[3px] rounded-full" style={{ background: '#b7c1cc' }} />
              <span className="inline-flex items-center gap-1 whitespace-nowrap"><PhoneCall className="h-[14px] w-[14px]" aria-hidden="true" /> Call within 2 hours</span>
              <i aria-hidden="true" className="h-[3px] w-[3px] rounded-full" style={{ background: '#b7c1cc' }} />
              <span className="inline-flex items-center gap-1 whitespace-nowrap"><ShieldCheck className="h-[14px] w-[14px]" aria-hidden="true" /> No spam ever</span>
            </div>
          </form>
        )}
      </section>
    </div>
  );
};

const defaultSubmitContact = async () => ({ success: true });

/**
 * Fixed entry point for the HR callback form.
 *
 * The transport is intentionally injectable. Pass submitContact from the
 * consuming page when the HR callback API contract is available.
 */
const HrContactLauncher = ({
  courseLabel = 'Java Fullstack',
  countryOptions = DEFAULT_COUNTRY_OPTIONS,
  submitContact = defaultSubmitContact,
  onOpen,
  onClose,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);

  const openModal = useCallback(() => {
    setIsOpen(true);
    onOpen?.();
  }, [onOpen]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  return (
    <>
      <div
        className="fixed z-[90] font-jost"
        style={{ right: 'max(18px, env(safe-area-inset-right))', bottom: 'max(18px, env(safe-area-inset-bottom))' }}
      >
        <button
          ref={triggerRef}
          type="button"
          className="relative inline-flex min-h-[52px] min-w-[44px] max-w-[calc(100vw-36px)] items-center gap-2.5 whitespace-nowrap rounded-full bg-[#25d366] py-2 pl-2.5 pr-[17px] text-[0.88rem] font-bold tracking-[0.01em] text-white shadow-[0_10px_26px_rgba(21,125,70,0.28)] transition-all duration-[180ms] hover:-translate-y-0.5 hover:bg-[#1fbd5a] hover:shadow-[0_14px_32px_rgba(21,125,70,0.34)] active:translate-y-0 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[#0d5ef4]/[0.22] focus-visible:ring-offset-2"
          style={{ border: '1px solid rgba(255, 255, 255, 0.55)' }}
          onClick={openModal}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-controls="hr-contact-dialog"
        >
          <span
            className="inline-flex h-9 w-9 items-center justify-center rounded-full"
            style={{ color: '#25d366', background: COLORS.surface }}
            aria-hidden="true"
          >
            <Headphones className="h-[19px] w-[19px]" />
          </span>
          <span>Talk with our HR Team</span>
          <span
            className="absolute right-[5px] top-[3px] h-[9px] w-[9px] animate-pulse rounded-full"
            style={{ background: '#ffcf33', border: '2px solid #25d366' }}
            aria-hidden="true"
          />
        </button>
      </div>

      {isOpen && typeof document !== 'undefined'
        ? createPortal(
            <HrContactModal
              isOpen={isOpen}
              onClose={closeModal}
              triggerRef={triggerRef}
              courseLabel={courseLabel}
              countryOptions={countryOptions}
              submitContact={submitContact}
            />,
            document.body,
          )
        : null}
    </>
  );
};

export default HrContactLauncher;
